<template>
  <div class="vis-view">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h4 font-weight-bold">Visual Identity Systems</h1>
      <div class="d-flex align-center">
        <v-btn 
          variant="outlined"
          color="black" 
          prepend-icon="mdi-plus"
          @click="openCreateModal"
          class="create-vis-btn"
        >
          CREATE VIS
        </v-btn>
      </div>
    </div>

    <div class="vis-tabs">
      <v-tabs v-model="activeTab" class="mb-4" color="#e94057">
        <v-tab value="all">ALL VIS</v-tab>
        <v-tab value="latest">LATEST</v-tab>
      </v-tabs>
    </div>
    
    <div class="vis-search mb-4">
      <v-text-field
        v-model="searchTerm"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search Visual Identity Systems"
        single-line
        hide-details
        variant="outlined"
        density="comfortable"
        @input="onSearchChange"
        bg-color="white"
        class="search-field"
      ></v-text-field>
    </div>
    
    <div v-if="loading" class="vis-grid">
      <div v-for="i in 6" :key="i" class="vis-grid-item">
        <div class="skeleton-card">
          <div class="skeleton-card-image skeleton-box"></div>
          <div class="skeleton-card-content">
            <div class="skeleton-box skeleton-title"></div>
            <div class="skeleton-box skeleton-subtitle"></div>
            <div class="skeleton-card-footer">
              <div class="skeleton-box skeleton-badge"></div>
              <div class="skeleton-box skeleton-date"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div v-else-if="error" class="vis-error">
      <p>{{ error }}</p>
      <v-btn @click="fetchVisualIdentitySystems" color="primary">Try Again</v-btn>
    </div>
    
    <div v-else-if="displayedVIS.length === 0" class="vis-empty">
      <p>No Visual Identity Systems found.</p>
      <v-btn @click="openCreateModal" color="primary">Create Your First VIS</v-btn>
    </div>
    
    <div v-else class="vis-grid">
      <div v-for="vis in displayedVIS" :key="vis.id" class="vis-grid-item">
        <VISCard 
          :visData="vis"
          @click="navigateToVISDetail(vis.id)"
        />
      </div>
    </div>
    
    <div v-if="displayedVIS.length > 0" class="vis-pagination">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        total-visible="7"
        @update:model-value="onPageChange"
      ></v-pagination>
    </div>

    <!-- Create VIS Modal -->
    <VISFormModal
      :showModal="showCreateModal"
      :visData="null"
      :isEditMode="false"
      @close="showCreateModal = false"
      @success="handleCreateSuccess"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import VISCard from '@/components/VISCard.vue';
import VISFormModal from '@/components/VISFormModal.vue';

export default {
  name: 'VisualIdentitySystemsView',
  components: {
    VISCard,
    VISFormModal
  },
  data() {
    return {
      activeTab: 'all',
      searchTerm: '',
      currentPage: 1,
      pageSize: 12,
      searchDebounce: null,
      
      // Create VIS Modal
      showCreateModal: false
    };
  },
  computed: {
    ...mapState({
      loading: state => state.vis.loading,
      error: state => state.vis.error,
      pagination: state => state.vis.pagination
    }),
    ...mapGetters({
      allVIS: 'vis/allVIS',
      latestVIS: 'vis/latestVIS',
      filteredVIS: 'vis/filteredVIS'
    }),
    totalPages() {
      return this.pagination.totalPages;
    },
    displayedVIS() {
      // Return different VIS lists based on active tab
      if (this.activeTab === 'all') {
        return this.filteredVIS;
      } else if (this.activeTab === 'latest') {
        // Show only the last three VIS entries in the Latest tab
        const filtered = this.searchTerm ? 
          this.latestVIS.filter(vis => this.filterVISBySearch(vis)) : 
          this.latestVIS;
        return filtered.slice(0, 3);
      }
      // Default fallback
      return this.filteredVIS;
    }
  },
  methods: {
    ...mapActions({
      fetchVISList: 'vis/fetchVISList',
      setFilters: 'vis/setFilters'
    }),
    // Navigation methods
    navigateToVISDetail(id) {
      this.$router.push({ name: 'visDetail', params: { id } });
    },
    
    // Create Modal methods
    openCreateModal() {
      this.showCreateModal = true;
    },
    
    // API calls
    async fetchVisualIdentitySystems() {
      try {
        await this.fetchVISList({
          page: this.currentPage,
          pageSize: this.pageSize
        });
      } catch (error) {
        console.error('Error fetching VIS:', error);
      }
    },
    
    onPageChange(page) {
      this.currentPage = page;
      this.fetchVisualIdentitySystems();
    },
    
    onSearchChange() {
      // Debounce search to avoid too many API calls
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.setFilters({ searchTerm: this.searchTerm });
      }, 300);
    },
    
    filterVISBySearch(vis) {
      if (!this.searchTerm) return true;
      
      const term = this.searchTerm.toLowerCase();
      return (
        vis.brand_name.toLowerCase().includes(term) ||
        vis.version.toLowerCase().includes(term)
      );
    },
    
    handleCreateSuccess() {
      this.fetchVisualIdentitySystems();
    }
  },
  watch: {
    activeTab() {
      // Reset pagination when tab changes
      this.currentPage = 1;
    }
  },
  created() {
    this.fetchVisualIdentitySystems();
  }
};
</script>

<style scoped>
.vis-view {
  padding: 24px;
  background-color: #f8f9fa;
}

.create-vis-btn {
  height: 40px;
  background-color: transparent !important;
  color: black;
  font-weight: bold;
  border-radius: 8px;
  border: 1px solid black;
}

.vis-tabs {
  margin-bottom: 20px;
}

.vis-search {
  margin-bottom: 20px;
}

.search-field {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.vis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.vis-grid-item {
  height: 100%;
}

.vis-error, .vis-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.vis-error p, .vis-empty p {
  margin: 20px 0;
  color: #666;
}

.vis-pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* Skeleton Card Styles */
.skeleton-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.skeleton-card-image {
  width: 100%;
  height: 180px;
  background: linear-gradient(
    90deg,
    #e0e0e0 0%,
    #f0f0f0 20%,
    #f0f0f0 40%,
    #e0e0e0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-card-content {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  gap: 12px;
}

.skeleton-box {
  background: linear-gradient(
    90deg,
    #e0e0e0 0%,
    #f0f0f0 20%,
    #f0f0f0 40%,
    #e0e0e0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title {
  height: 20px;
  width: 70%;
}

.skeleton-subtitle {
  height: 16px;
  width: 50%;
}

.skeleton-badge {
  height: 24px;
  width: 80px;
  border-radius: 12px;
}

.skeleton-date {
  height: 14px;
  width: 60px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style> 